import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by selecting the variant's media in slideshow.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */

  //   #handleVariantUpdate = (event) => {
  //   const source = event.detail.data.html;

  //   if (!source) return;
  //   const newMediaGallery = source.querySelector('media-gallery');

  //   if (!newMediaGallery) return;

  //   // PDP: No change needed here; perform a standard replace
  //   this.replaceWith(newMediaGallery);
  // };
  #handleVariantUpdate = (event) => {
    const featuredMediaId = event.detail.resource?.featured_media?.id;

    // If the variant's media is already rendered, keep the scroll behavior.
    if (featuredMediaId && this.slideshow && this.querySelector(`slideshow-slide[slide-id="${featuredMediaId}"]`)) {
      this.slideshow.select({ id: featuredMediaId }, undefined, { animate: false });
      return;
    }

    // Otherwise the gallery is variant-filtered (hide_variants) and the new
    // variant's media isn't in the DOM — restore Horizon's original behavior:
    // re-render this gallery from the already-fetched section HTML.
    const source = event.detail.data?.html;
    const newMediaGallery = source?.querySelector('media-gallery');
    if (newMediaGallery) this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
